import React from 'react';
import { useTranslation } from 'react-i18next';

import { useStreetBeatChat, useConversationFlow } from '@dev-streetbeat/chat';
import type { Message } from '@dev-streetbeat/chat';

import { ChatUserMessage } from './components/messages/ChatUserMessage';
import { ChatAgentMessage } from './components/messages/ChatAgentMessage';
import { AssistantActionCreatePortfolioType, AssistantActionSendEmailType } from 'types/AssistantTypes';
import { AI_PRO_ADVISOR } from 'constants/AiProAdvisor';
import { ExcelPastTwoColumnsWrapper } from './components/ExcelPastTwoColumnsWrapper';
import { ExcelPastOneColumnsWrapper } from './components/ExcelPastOneColumnsWrapper';
import SendEmailAction from './components/SendEmailAction';
import {
  convertSDKActionToOriginalAction,
  transformActionSubmissionData,
  type SDKAction,
  type ActionSubmissionData,
} from './utils/messageConversion';
import { MESSAGE_ROLES, MESSAGE_TYPES, SPECIAL_MESSAGE_IDS, ACTION_PAYLOAD, DEFAULT_VALUES } from './constants';

interface MessagesListProps {
  className?: string;
}

export const MessagesList: React.FC<MessagesListProps> = () => {
  const { t } = useTranslation();
  const { messages, sendMessage, streamingMessage, pendingAction, handleAction } = useStreetBeatChat();
  const { isLoading: runInProgress } = useConversationFlow();
  const hasPendingAction = !!pendingAction;

  // Stable payload preparation function
  const prepareActionPayload = React.useCallback((data: ActionSubmissionData) => {
    return transformActionSubmissionData(data);
  }, []);

  // Stable message send handler
  const handleMessageSend = React.useCallback(
    async (text: string) => {
      try {
        await sendMessage(text);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },
    [sendMessage],
  );

  // Action submission handler using SDK implementation
  const handleActionSubmission = React.useCallback(
    async (
      actionId: string,
      _threadId: string, // Kept for API compatibility but unused
      data: ActionSubmissionData,
    ): Promise<void> => {
      if (!handleAction) {
        throw new Error('Action handler not available - SDK not properly initialized');
      }

      try {
        const payload = prepareActionPayload(data);
        await handleAction(actionId, payload);
      } catch (error) {
        console.error('Action submission failed:', error instanceof Error ? error.message : 'Unknown error');
        throw error instanceof Error ? error : new Error('Action submission failed');
      }
    },
    [handleAction, prepareActionPayload],
  );

  // Memoize pending action conversion
  const convertedPendingAction = React.useMemo(() => {
    if (!hasPendingAction || !pendingAction) return null;
    return convertSDKActionToOriginalAction(pendingAction as unknown as SDKAction);
  }, [hasPendingAction, pendingAction]);

  // Stable message factory functions
  const createStreamingMessage = React.useCallback(
    (text: string) => ({
      id: SPECIAL_MESSAGE_IDS.STREAMING,
      thread_id: DEFAULT_VALUES.EMPTY_THREAD_ID,
      role: MESSAGE_ROLES.ASSISTANT,
      content: [
        {
          type: MESSAGE_TYPES.TEXT,
          text: { value: text },
        },
      ],
      artifacts: [],
      created_at: new Date().toISOString(),
    }),
    [],
  );

  const createSkeletonMessage = React.useCallback(
    (text: string) => ({
      id: SPECIAL_MESSAGE_IDS.SKELETON,
      thread_id: DEFAULT_VALUES.EMPTY_THREAD_ID,
      role: MESSAGE_ROLES.ASSISTANT,
      content: [
        {
          type: MESSAGE_TYPES.TEXT,
          text: { value: text },
        },
      ],
      artifacts: [],
      created_at: new Date().toISOString(),
    }),
    [],
  );

  // Create a combined list of messages and actions with memoized conversions
  const combinedItems = React.useMemo(() => {
    type CombinedItem = (Message | AssistantActionCreatePortfolioType | AssistantActionSendEmailType) & {
      object?: string;
      action?: {
        id: string;
        thread_id: string;
        type: string;
        input?: Record<string, unknown>;
        output?: unknown;
        status: string;
        expires_at?: string;
        created_at: string;
        updated_at?: string;
      };
    };
    const items: CombinedItem[] = [...messages] as CombinedItem[];

    // Add memoized pending action if it exists
    if (convertedPendingAction) {
      items.push(convertedPendingAction);
    }

    // Don't sort - trust the SDK message order
    return items;
  }, [messages, convertedPendingAction]);

  return (
    <>
      {/* Render combined messages and actions exactly like original */}
      {combinedItems.map((item) => {
        // Check if this is an action (has 'object' property)
        if (item.object) {
          switch (item.object) {
            case AI_PRO_ADVISOR.ANALYZE_PORTFOLIO_FROM_WEIGHTS:
              return (
                <ExcelPastTwoColumnsWrapper
                  key={item.action.id}
                  messagePortfolioCast={item as AssistantActionCreatePortfolioType}
                  onActionSubmit={handleActionSubmission}
                />
              );
            case AI_PRO_ADVISOR.SET_PRODUCTS:
              return (
                <ExcelPastOneColumnsWrapper
                  key={item.action.id}
                  messagePortfolioCast={item as AssistantActionCreatePortfolioType}
                  onActionSubmit={handleActionSubmission}
                />
              );
            case AI_PRO_ADVISOR.SEND_EMAIL:
              const emailAction = item as AssistantActionSendEmailType;
              return (
                <SendEmailAction
                  key={item.action.id}
                  thread_id={emailAction.action.thread_id}
                  actionId={emailAction.action.id}
                  to={emailAction.action.input.to}
                  subject={emailAction.action.input.subject}
                  messageText={emailAction.action.input.message_text}
                  attachments={[]}
                />
              );
          }
        }

        // Use SDK message directly
        const message = item as Message;

        switch (message.role) {
          case MESSAGE_ROLES.USER:
            return <ChatUserMessage key={message.id} message={message} />;
          case MESSAGE_ROLES.ASSISTANT:
            return (
              <ChatAgentMessage
                key={message.id}
                message={message}
                isConnectionOpen={true}
                onSend={handleMessageSend}
                disabled={false}
              />
            );
          default:
            return null;
        }
      })}

      {/* Show streaming message if available */}
      {streamingMessage && (
        <ChatAgentMessage
          message={createStreamingMessage(streamingMessage)}
          isConnectionOpen={true}
          onSend={handleMessageSend}
          disabled={false}
        />
      )}

      {/* Show skeleton when run in progress, no streaming message, and no pending action */}
      {runInProgress &&
        messages.length > 0 &&
        messages[messages.length - 1]?.role === MESSAGE_ROLES.USER &&
        !hasPendingAction &&
        !streamingMessage && (
          <ChatAgentMessage
            message={createSkeletonMessage(t('assistant.status.preProcessing'))}
            thinking={true}
            isConnectionOpen={true}
            onSend={handleMessageSend}
            disabled={true}
          />
        )}
    </>
  );
};
